import axios, { AxiosResponse, AxiosStatic, Canceler } from 'axios';
import { SearchParamNames } from '../models/enums';
import { LinkModel, SearchProductTileModel } from '../models/globals';

const client = axios.create();
client.defaults.timeout = 30 * 1000;

client.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.toJSON) {
            console.error(error.toJSON());
        }
        throw error;
    },
);

interface CancelableRequest<T> {
    cancel: Canceler;
    isCancel: AxiosStatic['isCancel'];
    promise: Promise<AxiosResponse<T>>;
}

const get = <T>(url: string): CancelableRequest<T> => {
    const { cancel, token: cancelToken } = axios.CancelToken.source();

    return {
        cancel,
        isCancel: axios.isCancel,
        promise: client.get<T>(url, { cancelToken }),
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const post = <T>(url: string, data?: any): CancelableRequest<T> => {
    const { cancel, token: cancelToken } = axios.CancelToken.source();

    return {
        cancel,
        isCancel: axios.isCancel,
        promise: client.post<T>(url, data, { cancelToken }),
    };
};

interface AutocompleteSearchResult {
    productLinks: LinkModel[];
    terms: string;
}

const getAutocompleteSearchResult = (
    autocompleteApiPath: string,
    terms: string,
): CancelableRequest<AutocompleteSearchResult> => {
    const { promise, ...request } = get<AutocompleteSearchResult>(
        `${autocompleteApiPath}?${new URLSearchParams({ [SearchParamNames.Query]: terms })}`,
    );

    return {
        ...request,
        promise: promise.then((response) => {
            if (!response.data.productLinks) {
                const error = new Error('Unexpected response');

                console.error(error, response);

                throw error;
            }

            return response;
        }),
    };
};

interface ProductSearchResult {
    productTiles: SearchProductTileModel[];
    terms: string;
}

const getProductSearchResult = (
    resultsApiPath: string,
    terms: string,
    courseTypes: Set<string>,
    subTopics: Set<string>,
): CancelableRequest<ProductSearchResult> => {
    const searchParams = new URLSearchParams({ [SearchParamNames.Query]: terms });
    courseTypes.forEach((type) => searchParams.append(SearchParamNames.CourseTypes, type));
    subTopics.forEach((code) => searchParams.append(SearchParamNames.SubTopics, code));
    const { promise, ...request } = get<ProductSearchResult>(`${resultsApiPath}?${searchParams}`);

    return {
        ...request,
        promise: promise.then((response) => {
            if (!response.data.productTiles) {
                const error = new Error('Unexpected response');

                console.error(error, response);

                throw error;
            }

            return response;
        }),
    };
};

type VimeoUrlResponse = unknown;

const checkIfVimeoVideoExists = (vimeoUrl: string): CancelableRequest<VimeoUrlResponse> => {
    return get<VimeoUrlResponse>(`https://vimeo.com/api/oembed.json?url=${vimeoUrl}`);
};

interface LeadFormResponse {
    analyticsData: {
        client_id: string; // Hashed email from form input
    };
}

const postLeadForm = (leadCreationApiPath: string, data: string): CancelableRequest<LeadFormResponse> => {
    return post<LeadFormResponse>(leadCreationApiPath, data);
};

const xhrClient = {
    getAutocompleteSearchResult,
    getProductSearchResult,
    checkIfVimeoVideoExists,
    postLeadForm,
};

export {
    xhrClient,
    CancelableRequest,
    AutocompleteSearchResult,
    ProductSearchResult,
    VimeoUrlResponse,
    LeadFormResponse,
};
